import type { CSSProperties } from 'react';
import { PipelineCanvasEdge, PipelineCanvasEdgeKind } from './graph/types';

/**
 * The reviewer graph's palette, from the app's existing canvas colours: words
 * violet (the prompt colour), pictures sky, criteria cyan, and the verdict fork
 * in the app-wide state semantics — emerald done, amber attention, red failed.
 * Exported so the reviewer's nodes can take their left-border accent from the
 * very colour their edge is drawn in.
 */
export const REVIEW_EDGE_COLORS: Record<string, string> = {
  review_text: '#c084fc',
  review_image: '#38bdf8',
  review_check: '#22d3ee',
  review_pass: '#34d399',
  review_uncertain: '#fbbf24',
  review_fail: '#f87171',
};

/**
 * Reviewer edges do not read as wiring: nothing here was typed by a user and
 * nothing can be re-pointed, so they are drawn as FLOW — a translucent stroke
 * with a soft glow of its own colour, no arrowhead. (Nothing on this canvas
 * animates; styleEdges pins `animated: false`.)
 */
const reviewEdgeStyle = (kind: string): CSSProperties => {
  const color = REVIEW_EDGE_COLORS[kind];
  return {
    stroke: color,
    strokeWidth: 2,
    strokeOpacity: 0.55,
    strokeLinecap: 'round',
    filter: `drop-shadow(0 0 3px ${color}55)`,
  };
};

/**
 * Edge look by kind. Data flow (mapping/asset) reads solid and accented;
 * configuration flow (param/cfg) reads thin amber dashed so the params column's
 * fan-out never competes with the pipeline's actual shape; derived/structural
 * edges are gray dashed because the user cannot edit them; `review_*` edges are
 * the reviewer graph's glassy flows.
 */
const EDGE_STYLES: Record<PipelineCanvasEdgeKind, CSSProperties> = {
  mapping: { stroke: 'var(--accent-strong)', strokeWidth: 2 },
  asset: { stroke: '#34d399', strokeWidth: 3 },
  param: { stroke: '#fbbf24', strokeWidth: 1, strokeDasharray: '4 3' },
  cfg: { stroke: '#fbbf24', strokeWidth: 1, strokeDasharray: '4 3' },
  derived: { stroke: 'var(--border-strong)', strokeWidth: 1.5, strokeDasharray: '6 4' },
  review_text: reviewEdgeStyle('review_text'),
  review_image: reviewEdgeStyle('review_image'),
  review_check: reviewEdgeStyle('review_check'),
  review_pass: reviewEdgeStyle('review_pass'),
  review_uncertain: reviewEdgeStyle('review_uncertain'),
  review_fail: reviewEdgeStyle('review_fail'),
};

export const edgeStyleForKind = (kind?: PipelineCanvasEdgeKind): CSSProperties =>
  EDGE_STYLES[kind || 'mapping'] || EDGE_STYLES.mapping;

/** The custom edge component that carries the X (remove) affordance. */
export const EDITABLE_EDGE_TYPE = 'editable';

/** Read-only structural edges: no affordance, tooltip only. Deliberately NOT
 *  `EDITABLE_EDGE_TYPE` — the type is what keeps the two behaviours apart. */
export const DERIVED_EDGE_TYPE = 'derived-structural';

/** True for edges the user may re-point or delete (everything but derived). */
export const isEditableEdge = (edge: PipelineCanvasEdge): boolean =>
  !(edge.data?.derived || edge.data?.kind === 'derived');

/**
 * Applies the per-kind style without touching the derivation layer's output,
 * and settles interactivity: derived/structural edges are not selectable, not
 * focusable and not deletable, so neither the Delete key nor an affordance can
 * reach them (they render as DERIVED_EDGE_TYPE, which draws a tooltip and
 * nothing else); everything else renders as the editable edge type.
 */
export const styleEdges = (edges: PipelineCanvasEdge[]): PipelineCanvasEdge[] =>
  edges.map((edge) => {
    const editable = isEditableEdge(edge);
    return {
      ...edge,
      animated: false,
      type: editable ? EDITABLE_EDGE_TYPE : DERIVED_EDGE_TYPE,
      selectable: editable,
      focusable: editable,
      deletable: editable,
      style: { ...edgeStyleForKind(edge.data?.kind), ...(edge.style || {}) },
    };
  });
